import { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { submitPreventivo } from "@/lib/api";

const serramentoTypes = [
  { value: "finestre", label: "Finestre" },
  { value: "porte-finestre", label: "Porte Finestre" },
  { value: "infissi", label: "Infissi" },
  { value: "persiane", label: "Persiane" },
  { value: "altro", label: "Altro" },
];

const EMPTY = {
  nome: "",
  email: "",
  telefono: "",
  tipo_serramento: "",
  dimensioni: "",
  quantita: "",
  messaggio: "",
  hp: "",
};

export default function QuoteSection() {
  const [formData, setFormData] = useState(EMPTY);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, tipo_serramento: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitPreventivo(formData);
      setIsSubmitted(true);
      toast.success("Richiesta inviata con successo! Ti contatteremo presto.");
      setFormData(EMPTY);
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error(error.message || "Errore nell'invio. Riprova o contattaci direttamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="preventivo" className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-lg border border-[#E2E8F0]">
            <div className="w-20 h-20 bg-[#4CD964]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#4CD964]" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0B1E36] mb-4">
              Richiesta Inviata!
            </h2>
            <p className="text-[#64748B] text-lg mb-8">
              Grazie per averci contattato. Ti risponderemo entro 24 ore lavorative.
            </p>
            <Button 
              onClick={() => setIsSubmitted(false)}
              className="bg-[#0B1E36] hover:bg-[#0B1E36]/90 text-white font-semibold px-8 py-6 rounded-full"
              data-testid="quote-new-request-button"
            >
              Invia un&apos;altra richiesta
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="preventivo" className="py-24 md:py-32 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Content */}
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] bg-clip-text text-transparent bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 mb-4">
              Preventivo Gratuito
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0B1E36] mb-6">
              Richiedi un Preventivo Personalizzato
            </h2>
            <p className="text-base md:text-lg text-[#64748B] leading-relaxed mb-8">
              Compila il modulo con i dettagli del tuo progetto e riceverai un preventivo 
              dettagliato entro 24 ore. Il nostro team è a tua disposizione per qualsiasi domanda.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#007AFF]/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#007AFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-[#0B1E36] mb-1">Sopralluogo Gratuito</h4>
                  <p className="text-[#64748B] text-sm">Un nostro tecnico verrà a casa tua per valutare il progetto.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#FF9500]/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#FF9500]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-[#0B1E36] mb-1">Risposta in 24h</h4>
                  <p className="text-[#64748B] text-sm">Ti ricontatteremo entro un giorno lavorativo.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#4CD964]/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#4CD964]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-[#0B1E36] mb-1">Senza Impegno</h4>
                  <p className="text-[#64748B] text-sm">Il preventivo non comporta alcun obbligo di acquisto.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-[#E2E8F0]">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
              {/* Honeypot anti-bot: nascosto agli umani, se compilato → richiesta ignorata */}
              <input
                type="text"
                name="hp"
                value={formData.hp}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 0, height: 0, opacity: 0 }}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="nome" className="text-[#0B1E36] font-medium">Nome Completo *</Label>
                  <Input
                    id="nome"
                    name="nome"
                    type="text"
                    required
                    value={formData.nome}
                    onChange={handleChange}
                    placeholder="Mario Rossi"
                    className="h-12 border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]/10 rounded-xl"
                    data-testid="quote-nome-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#0B1E36] font-medium">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="mario@email.com"
                    className="h-12 border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]/10 rounded-xl"
                    data-testid="quote-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="telefono" className="text-[#0B1E36] font-medium">Telefono *</Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    required
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="+39 333 1234567"
                    className="h-12 border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]/10 rounded-xl"
                    data-testid="quote-telefono-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0B1E36] font-medium">Tipo di Serramento *</Label>
                  <Select value={formData.tipo_serramento} onValueChange={handleSelectChange} required>
                    <SelectTrigger 
                      className="h-12 border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]/10 rounded-xl"
                      data-testid="quote-tipo-select"
                    >
                      <SelectValue placeholder="Seleziona tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {serramentoTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="dimensioni" className="text-[#0B1E36] font-medium">Dimensioni (opzionale)</Label>
                  <Input
                    id="dimensioni"
                    name="dimensioni"
                    type="text"
                    value={formData.dimensioni}
                    onChange={handleChange}
                    placeholder="es. 120x140 cm"
                    className="h-12 border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]/10 rounded-xl"
                    data-testid="quote-dimensioni-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quantita" className="text-[#0B1E36] font-medium">Quantità (opzionale)</Label>
                  <Input
                    id="quantita"
                    name="quantita"
                    type="text"
                    value={formData.quantita}
                    onChange={handleChange}
                    placeholder="es. 4 finestre"
                    className="h-12 border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]/10 rounded-xl"
                    data-testid="quote-quantita-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="messaggio" className="text-[#0B1E36] font-medium">Descrivi il tuo progetto *</Label>
                <Textarea
                  id="messaggio"
                  name="messaggio"
                  required
                  value={formData.messaggio}
                  onChange={handleChange}
                  placeholder="Descrivi le tue esigenze, il tipo di immobile, eventuali preferenze su materiali o colori..."
                  className="min-h-[120px] border-[#E2E8F0] focus:border-[#0B1E36] focus:ring-[#0B1E36]/10 rounded-xl resize-none"
                  data-testid="quote-messaggio-input"
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-shine bg-[#0B1E36] hover:bg-[#0B1E36]/90 text-white font-semibold h-14 rounded-full text-base flex items-center justify-center gap-2"
                data-testid="quote-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Invio in corso...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Invia Richiesta Preventivo
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
